/**
 * keyboardScopeStack — pure data-structure layer for the WS-NAV-5
 * keyboard SSOT. No React, no DOM, no module singletons exposed in
 * the public surface; the stack is owned by `useKeyboardScope` and
 * exercised in isolation by this file's unit tests.
 *
 * Why a stack
 * -----------
 * Phase 1+ of the j/k/a/s/n/c/d/? chain will layer scopes on top of
 * each other (palette > modal > list > shell), and ONLY the topmost
 * scope owns the keydown. Anything else routes through a global
 * window listener which has two failure modes the plan calls out
 * (see /Users/yongjie/.claude/plans/ navigation-5 R6):
 *   1. multiple components attach independent listeners and fire in
 *      undefined order ("which n won?"), and
 *   2. modals leak their keybinds back to the underlying surface
 *      when they close in the wrong order.
 *
 * A stack with last-push-wins dispatch is the smallest invariant
 * that solves both — push on open, pop on close, only `peek()`
 * receives events. The Phase 0 module ships the stack + dispatch +
 * ESC discipline so NAV-2 (and the rest of NAV-5) can consume it
 * without re-litigating the contract.
 *
 * ESC semantics
 * -------------
 * ESC is the only key that has a stack-aware default: if the topmost
 * scope does NOT register an Escape handler, dispatching ESC pops
 * the scope (mirrors the "modals close on ESC" expectation across
 * the app). If the topmost scope DOES register Escape, that handler
 * runs first and the scope is NOT auto-popped — the handler decides.
 *
 * Key combo grammar
 * -----------------
 * Combos are normalised lowercase strings:
 *   - bare key: `"j"`, `"escape"`, `"arrowdown"`
 *   - with modifiers: `"mod+k"` (mod = meta on mac, ctrl elsewhere —
 *     the lookup tries both), `"shift+/"`, `"ctrl+shift+p"`
 * The hook layer is responsible for producing the lookup string from
 * the raw KeyboardEvent; this module just consumes the resulting
 * string and matches against the topmost scope's Map.
 */

import { captureError } from '../errors/captureError';

/** A single key-combo binding. The handler receives the raw event so
 *  it can call `preventDefault` / `stopPropagation` as needed. */
export type KeyComboShortcut = {
    /** Normalised combo string — see module header for grammar. */
    combo: string;
    /** Invoked when the topmost scope owns the dispatch + the combo
     *  matches. Throwing is captured via `captureError` and does NOT
     *  propagate to other handlers in the same stack. */
    handler: (event: KeyboardEvent) => void;
};

/** A registered scope on the stack. `id` is the stable identity
 *  (driven by React `useId()` so StrictMode double-invoke is a
 *  no-op), `owner` is a free-form debug label. */
export type KeyboardScope = {
    readonly id: string;
    readonly owner: string;
    readonly shortcuts: ReadonlyMap<string, KeyComboShortcut['handler']>;
};

/** Build the immutable shortcut map from the caller-supplied list.
 *  Last-write-wins on duplicate combos within a single scope so the
 *  consumer can declare a conditional binding without branching at
 *  the call site. */
export function buildShortcutMap(
    shortcuts: ReadonlyArray<KeyComboShortcut>,
): ReadonlyMap<string, KeyComboShortcut['handler']> {
    const map = new Map<string, KeyComboShortcut['handler']>();
    for (const s of shortcuts) {
        map.set(s.combo.toLowerCase(), s.handler);
    }
    return map;
}

/** The mutable stack. Exposed via `createKeyboardScopeStack()` so
 *  tests get their own instance — the module-singleton version lives
 *  on top of this in `useKeyboardScope`. */
export type KeyboardScopeStack = {
    push: (scope: KeyboardScope) => () => void;
    pop: (id: string) => void;
    peek: () => KeyboardScope | null;
    isEmpty: () => boolean;
    count: () => number;
    /** Dispatch a normalised combo to the topmost scope.
     *  Returns true iff a handler ran (or ESC auto-popped a scope).
     *  Caller is responsible for `preventDefault` if needed. */
    dispatch: (combo: string, event: KeyboardEvent) => boolean;
};

/** Construct a fresh stack instance. Tests use this directly; the
 *  hook builds one at module load time. */
export function createKeyboardScopeStack(): KeyboardScopeStack {
    const stack: KeyboardScope[] = [];

    const pop = (id: string): void => {
        // Splice by id (not just pop) so out-of-order unmount is
        // safe — React 19 commit order can interleave when two
        // siblings unmount in the same flush. See R6 in the plan.
        const idx = stack.findIndex((s) => s.id === id);
        if (idx >= 0) {
            stack.splice(idx, 1);
        }
    };

    const peek = (): KeyboardScope | null => {
        const last = stack[stack.length - 1];
        return last ?? null;
    };

    const push = (scope: KeyboardScope): (() => void) => {
        // Re-push of an existing id is a no-op (StrictMode double-
        // invoke / hot-reload safety). The popper still works.
        if (!stack.some((s) => s.id === scope.id)) {
            stack.push(scope);
        }
        return () => pop(scope.id);
    };

    const dispatch = (combo: string, event: KeyboardEvent): boolean => {
        const top = peek();
        if (!top) return false;

        const normalised = combo.toLowerCase();
        const handler = top.shortcuts.get(normalised);

        if (handler) {
            try {
                handler(event);
            } catch (err) {
                captureError(err, {
                    scope: 'keyboardScopeStack',
                    op: `dispatch:${top.owner}:${normalised}`,
                });
            }
            return true;
        }

        // ESC fallback: if the topmost scope didn't register an
        // Escape handler, pop it. Mirrors the "modals close on ESC"
        // expectation across the surface.
        if (normalised === 'escape') {
            pop(top.id);
            return true;
        }

        return false;
    };

    return {
        push,
        pop,
        peek,
        isEmpty: () => stack.length === 0,
        count: () => stack.length,
        dispatch,
    };
}
